import { api, handleApiError } from '../client'

// Types
export type IndustryType =
  | 'General' | 'Legal' | 'Finance' | 'Healthcare' | 'Technology'
  | 'Marketing' | 'RealEstate' | 'Education' | 'Consulting'
  | 'Manufacturing' | 'Retail' | 'Insurance' | 'NonProfit'
  | 'Government' | 'Architecture' | 'HumanResources'

export type PresetComplexity = 'Basic' | 'Standard' | 'Advanced' | 'Enterprise'

export interface IndustryPresetSummary {
  id: string
  name: string
  slug: string
  description: string
  industry: IndustryType
  icon: string
  color: string
  complexity: PresetComplexity
  estimatedSetupMinutes: number
  targetRoles: string[]
  isFeatured: boolean
  agentCount: number
  workflowCount: number
  templateCount: number
  applicationCount: number
}

export interface AgentPresetSummary {
  slug: string
  name: string
  description: string
  emoji: string
  color: string
  personality: string
  skillCount: number
}

export interface WorkflowPresetSummary {
  name: string
  description: string
  category: string
  stepCount: number
  triggerType: string
}

export interface TemplatePresetSummary {
  name: string
  description: string
  templateType: string
  category: string
  variableCount: number
}

export interface BrandSettingsSummary {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  primaryFont: string
  tone: string
  formalityLevel: number
}

export interface FolderSummary {
  name: string
  path: string
  description?: string
  icon?: string
  childCount: number
}

export interface IndustryPreset extends IndustryPresetSummary {
  agents: AgentPresetSummary[]
  workflows: WorkflowPresetSummary[]
  templates: TemplatePresetSummary[]
  brandSettings?: BrandSettingsSummary
  folderStructure: FolderSummary[]
  createdAt: string
  modifiedAt?: string
  lastAppliedAt?: string
}

export interface PresetPreview {
  preset: IndustryPresetSummary
  agentsToCreate: AgentPresetSummary[]
  workflowsToCreate: WorkflowPresetSummary[]
  templatesToCreate: TemplatePresetSummary[]
  foldersToCreate: FolderSummary[]
  existingConflicts: {
    agents: string[]
    workflows: string[]
    templates: string[]
    folders: string[]
  }
  estimatedResources: {
    totalAgents: number
    totalWorkflows: number
    totalTemplates: number
    totalFolders: number
  }
}

export interface ApplyPresetOptions {
  skipExisting?: boolean
  applyBrandSettings?: boolean
  selectedAgents?: string[]
  selectedWorkflows?: string[]
  selectedTemplates?: string[]
}

export interface PresetApplicationResult {
  success: boolean
  presetId: string
  presetName: string
  createdAgents: { slug: string; name: string; id: string }[]
  createdWorkflows: { name: string; id: string }[]
  createdTemplates: { name: string; id: string }[]
  createdFolders: { name: string; id: string }[]
  skippedItems: string[]
  errors: string[]
  appliedAt: string
}

// API Service
export const industryPresetsService = {
  /**
   * Get all available industry presets.
   */
  async getAll(activeOnly = true): Promise<IndustryPresetSummary[]> {
    try {
      const response = await api.get<IndustryPresetSummary[]>(
        `/v1/industry-presets`,
        { params: { activeOnly } }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  /**
   * Get a preset by ID.
   */
  async getById(id: string): Promise<IndustryPreset> {
    try {
      const response = await api.get<IndustryPreset>(`/v1/industry-presets/${id}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  /**
   * Get a preset by slug.
   */
  async getBySlug(slug: string): Promise<IndustryPreset> {
    try {
      const response = await api.get<IndustryPreset>(`/v1/industry-presets/by-slug/${slug}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  /**
   * Get a preview of what will be created when applying a preset.
   */
  async getPreview(id: string): Promise<PresetPreview> {
    try {
      const response = await api.get<PresetPreview>(`/v1/industry-presets/${id}/preview`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  /**
   * Apply a preset to the current tenant.
   */
  async apply(id: string, options?: ApplyPresetOptions): Promise<PresetApplicationResult> {
    try {
      const response = await api.post<PresetApplicationResult>(
        `/v1/industry-presets/${id}/apply`,
        options || {}
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  /**
   * Get featured presets for display on selection screen.
   */
  async getFeatured(): Promise<IndustryPresetSummary[]> {
    try {
      const all = await this.getAll(true)
      return all.filter(p => p.isFeatured).slice(0, 6)
    } catch (error) {
      throw handleApiError(error)
    }
  }
}

export default industryPresetsService
